import { useCallback, useEffect, useState } from 'react';
import {
  createDealFromSubmission,
  createFundingDeal,
  deleteFundingDeal,
  fetchClientFundingDeals,
  fetchFundingDeal,
  fetchFundingDeals,
  updateFundingDeal,
} from '../../services/funding/fundingDealsService';

export function useFundingDeals({ agencyId, clientId, filters = {}, autoLoad = true } = {}) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = clientId && agencyId
        ? await fetchClientFundingDeals(clientId, agencyId)
        : await fetchFundingDeals({
          agencyId,
          clientId,
          stage: filters.stage,
          lenderType: filters.lenderType,
          productType: filters.productType,
          assignedUserId: filters.assignedUserId,
          search: filters.search,
        });
      setDeals(result.deals);
      return result.deals;
    } catch (err) {
      setError(err?.message || 'Failed to load deals');
      setDeals([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [agencyId, clientId, filters.stage, filters.lenderType, filters.productType, filters.assignedUserId, filters.search]);

  const create = useCallback(async (payload, targetClientId, targetAgencyId) => {
    const cid = targetClientId || clientId;
    const aid = targetAgencyId || agencyId;
    if (!cid || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createFundingDeal(cid, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create deal');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, clientId, load]);

  const createFromSubmission = useCallback(async (submissionId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!submissionId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await createDealFromSubmission(submissionId, aid);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to create deal from submission');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const update = useCallback(async (dealId, payload, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!dealId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      const result = await updateFundingDeal(dealId, aid, payload);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to update deal');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  const remove = useCallback(async (dealId, targetAgencyId) => {
    const aid = targetAgencyId || agencyId;
    if (!dealId || !aid) return null;

    setSaving(true);
    setError(null);
    try {
      await deleteFundingDeal(dealId, aid);
      await load();
      return true;
    } catch (err) {
      setError(err?.message || 'Failed to withdraw deal');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [agencyId, load]);

  useEffect(() => {
    if (autoLoad && (agencyId || clientId)) {
      load();
    }
  }, [autoLoad, load, agencyId, clientId]);

  return {
    deals,
    loading,
    saving,
    error,
    load,
    create,
    createFromSubmission,
    update,
    remove,
    setDeals,
  };
}

export function useFundingDealDetail({ dealId, agencyId, autoLoad = true } = {}) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!dealId || !agencyId) {
      setDeal(null);
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingDeal(dealId, agencyId);
      setDeal(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load deal');
      setDeal(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [dealId, agencyId]);

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad, load]);

  return { deal, loading, error, load, setDeal };
}
